import {
    getLiquidStakingPoolInfo, getJettonWalletsByMasters, runGetMethod, readStackNumber,
} from '~/api/toncenterV3';

// Liquid staking jettons whose TON value the address header shows as a
// separate "Staking" row, keyed by the raw jetton master address as it comes
// in /jetton/wallets responses. The pool is the minter's admin; it is hard
// coded so that no request is spent on looking it up. The name is only there
// to tell the entries apart. The pool runs the
// ton-blockchain/liquid-staking-contract code.
export const LIQUID_STAKING_JETTONS = Object.freeze({
    '0:AA0BA121449FEDA569E02B12FA755D24E834A7454AECF4649590B6DF742AAC8F': { // STAKED
        pool: 'EQD2_4d91M4TVbEBVyBF8J1UwpMJc361LKVCz6bBlffMW05o',
        name: 'Stakee',
    },
});

// Liquid staking jettons of other protocols. They get no row of their own:
// their GRAM value is added to the balance that the "per year" estimate is
// counted from. The rate is GRAM held / jettons issued, read by `method` on
// `contract` from the stack entries `coins` and `supply`.
export const OTHER_LIQUID_STAKING_JETTONS = Object.freeze({
    '0:BDF3FA8098D129B54B4F73B5BAC5D1E1FD91EB054169C3916DFC8CCD536D1000': { // tsTON, Tonstakers
        contract: 'EQCkWxfyhAkim3g2DjKQQg8T5P4g-Q1-K_jErGcDJZ4i-vqR', method: 'get_pool_full_data', coins: 2, supply: 13,
    },
    '0:6E2215CD36459FF405BD9A234635348EFEE159DB77A37C85AB89E5E07B97FBDF': { // KTON, newer pool code with 34 entries
        contract: 'EQA9HwEZD_tONfVz6lJS0PVKR5viEiEGyj9AuQewGQVnXPg0', method: 'get_pool_full_data', coins: 2, supply: 17,
    },
    '0:CF76AF318C0872B58A9F1925FC29C156211782B9FB01F56760D292E56123BF87': { // hTON, Hipo treasury
        contract: '0:8BC991CFE177BC7E9721433EFA3BEFD199485A55CFFD040A06C89AF026B71BCF', method: 'get_treasury_state', coins: 0, supply: 1,
    },
    '0:CD872FA7C5816052ACDF5332260443FAEC9AACC8C21CCA4D92E7F47034D11892': { // stTON, bemo
        contract: 'EQDNhy-nxYFgUqzfUzImBEP67JqsyMIcyk2S5_RwNNEYku0k', method: 'get_full_data', coins: 1, supply: 0,
    },
    '0:92C4664F1EA6B74ED9CE0E031A9FC0843348DFE87A58FAEA27FCD31E1608CAAA': { // bmTON, bemo
        contract: 'EQCSxGZPHqa3TtnODgMan8CEM0jf6HpY-uon_NMeFgjKqkEY', method: 'get_full_data', coins: 1, supply: 0,
    },
});

// The rate only moves once per validation round (~18 hours), so one read
// per pool serves every address page opened within the hour.
const RATE_TTL_MS = 60 * 60 * 1000;

const rateCache = new Map(); // contract address -> { promise, expires }

/**
 * Caches `load()` under `address`. A failed read is not cached, the next
 * call tries again.
 *
 * @param  {String} address
 * @param  {Function} load  resolves with TON per one jetton
 * @return {Promise<Number>}
 */
const getCachedRate = function (address, load) {
    const cached = rateCache.get(address);

    if (cached && cached.expires > Date.now()) {
        return cached.promise;
    }

    const promise = load()
        .then((rate) => {
            if (!(rate > 0)) {
                throw new Error(`Liquid staking contract ${address} has no rate`);
            }
            return rate;
        })
        .catch((error) => {
            rateCache.delete(address);
            throw error;
        });

    rateCache.set(address, { promise, expires: Date.now() + RATE_TTL_MS });

    return promise;
};

/**
 * TON per one pool jetton, i.e. total_balance / supply of the pool.
 *
 * @param  {String} pool
 * @return {Promise<Number>}
 */
export const getLiquidStakingRate = function (pool) {
    return getCachedRate(pool, () => getLiquidStakingPoolInfo(pool).then(info => info.rate));
};

const getOtherLiquidStakingRate = function ({ contract, method, coins, supply }) {
    return getCachedRate(contract, async () => {
        const { exit_code, stack } = await runGetMethod(contract, method);

        if (exit_code !== 0) {
            throw new Error(`${method} failed on ${contract}: exit code ${exit_code}`);
        }

        return readStackNumber(stack[coins]) / readStackNumber(stack[supply]);
    });
};

/**
 * Sums the TON value of an address' liquid staking jettons.
 *
 * @param  {Array} jettonWallets  as in /jetton/wallets, only the matching ones are used
 * @return {Promise<{ nanotons: Number }>}
 */
export const getStakedBalance = async function (jettonWallets) {
    const staked = (jettonWallets || [])
        .filter(wallet => LIQUID_STAKING_JETTONS[wallet.jetton] && Number(wallet.balance) > 0);

    let nanotons = 0;

    // Pools are read one by one; failures are skipped so that one halted
    // pool doesn't hide the other balances.
    // eslint-disable-next-line no-restricted-syntax
    for (const wallet of staked) {
        const { pool } = LIQUID_STAKING_JETTONS[wallet.jetton];

        try {
            // eslint-disable-next-line no-await-in-loop
            const rate = await getLiquidStakingRate(pool);
            nanotons += Number(wallet.balance) * rate;
        } catch (error) {
            console.error(error);
        }
    }

    return { nanotons };
};

/**
 * Sums the TON value of an address' liquid staking jettons of other
 * protocols (OTHER_LIQUID_STAKING_JETTONS). Failed rates are skipped.
 *
 * @param  {String} address
 * @return {Promise<Number>}  nanotons
 */
export const getOtherStakedNanotons = async function (address) {
    const wallets = await getJettonWalletsByMasters(address, Object.keys(OTHER_LIQUID_STAKING_JETTONS));

    const values = await Promise.all(wallets.map(async (wallet) => {
        const jetton = OTHER_LIQUID_STAKING_JETTONS[wallet.jetton];

        if (!jetton || !(Number(wallet.balance) > 0)) return 0;

        try {
            return Number(wallet.balance) * await getOtherLiquidStakingRate(jetton);
        } catch (error) {
            console.error(error);
            return 0;
        }
    }));

    return values.reduce((sum, value) => sum + value, 0);
};
